import React from "react";

/**
 * Sprint 60.131 — What-If Scenario side-by-side compare.
 * Computes per-player exposure across both batches and renders a
 * diff table (delta highlighted in green/red). Extracted from
 * RawOptimizer.jsx in Sprint 60.132 as part of the sub-component split.
 */
export default function ScenarioCompareView({ currentLineups, scenarioName, scenarioLineups, onClose }) {
  const compare = React.useMemo(() => {
    const countExposure = (lus) => {
      const counts = new Map();
      lus.forEach((L) => {
        (L.players || []).forEach((p) => {
          const key = String(p.dk_id);
          const entry = counts.get(key) || {
            dk_id: key, name: p.name, position: p.position, team: p.team, salary: p.salary, uses: 0,
          };
          entry.uses += 1;
          counts.set(key, entry);
        });
      });
      return counts;
    };
    const a = countExposure(currentLineups);
    const b = countExposure(scenarioLineups);
    const allIds = new Set([...a.keys(), ...b.keys()]);
    const rows = Array.from(allIds).map((id) => {
      const ea = a.get(id);
      const eb = b.get(id);
      const meta = ea || eb;
      const aUses = ea?.uses || 0;
      const bUses = eb?.uses || 0;
      const aPct = currentLineups.length > 0 ? (aUses / currentLineups.length) * 100 : 0;
      const bPct = scenarioLineups.length > 0 ? (bUses / scenarioLineups.length) * 100 : 0;
      return { ...meta, aUses, bUses, aPct, bPct, delta: aPct - bPct };
    });
    rows.sort((r1, r2) => Math.abs(r2.delta) - Math.abs(r1.delta));
    return rows;
  }, [currentLineups, scenarioLineups]);

  return (
    <div data-testid="raw-scenario-compare" className="overflow-x-auto max-h-[600px]">
      <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-3 sticky top-0 bg-black py-2 z-10 flex-wrap">
        <span>Comparing <span className="text-[#39FF14]">Current ({currentLineups.length})</span> vs <span className="text-[#FFCC00]">"{scenarioName}" ({scenarioLineups.length})</span></span>
        <span className="text-zinc-600">·</span>
        <span>{compare.length} unique players</span>
        <button
          type="button"
          onClick={onClose}
          data-testid="raw-scenario-compare-close"
          className="ml-auto text-zinc-500 hover:text-white border border-zinc-700 hover:border-white px-2 py-0.5 text-[10px] mono uppercase tracking-widest"
        >
          Close Compare
        </button>
      </div>
      <table className="w-full min-w-[720px] text-xs">
        <thead className="bg-black text-zinc-500 mono text-[9px] uppercase tracking-widest sticky top-8 z-10">
          <tr>
            <th className="p-2 text-left">Player</th>
            <th className="p-2 text-left">Pos·Team</th>
            <th className="p-2 text-right">Current %</th>
            <th className="p-2 text-right">"{scenarioName}" %</th>
            <th className="p-2 text-right">Δ</th>
          </tr>
        </thead>
        <tbody>
          {compare.map((r) => (
            <tr key={r.dk_id} className="border-t border-zinc-900 hover:bg-zinc-900/40">
              <td className="p-2 text-white truncate max-w-[200px]">{r.name}</td>
              <td className="p-2 text-zinc-400">{r.position} · {r.team}</td>
              <td className="p-2 text-right mono text-[#39FF14]">{r.aPct.toFixed(1)}</td>
              <td className="p-2 text-right mono text-[#FFCC00]">{r.bPct.toFixed(1)}</td>
              <td className={`p-2 text-right mono font-bold ${r.delta > 0 ? "text-[#39FF14]" : r.delta < 0 ? "text-[#FF3B30]" : "text-zinc-500"}`}>
                {r.delta > 0 ? "+" : ""}{r.delta.toFixed(1)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
